import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Paper,
  IconButton,
  CircularProgress,
  Pagination,
} from "@mui/material";
import {
  Close as CloseIcon,
  Map as MapIcon,
  Add as AddIcon,
  Bookmark as BookmarkIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";

const BLUE = "#1976d2";

function formatCreated(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString();
}

/** bookmark tool drawer panel — bookmarks user-wise server par store hote hain, paged list */
export default function BookmarkToolPanel(ctx) {
  const {
    bookmarkName,
    setBookmarkName,
    bookmarks,
    bookmarkPage,
    bookmarkPages,
    bookmarkTotal,
    bookmarkLoading,
    bookmarkError,
    loadBookmarks,
    handleSaveBookmark,
    handleGoToBookmark,
    handleDeleteBookmark,
    onCloseTool,
  } = ctx;

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: BLUE }}>
        Bookmarks
      </Typography>

      {/* Add Bookmark */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
          Bookmark Name
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Enter bookmark name"
          value={bookmarkName}
          onChange={(e) => setBookmarkName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !bookmarkLoading) handleSaveBookmark();
          }}
          inputProps={{ maxLength: 100 }}
          sx={{ mb: 1, bgcolor: "white" }}
        />
        <Button
          fullWidth
          variant="contained"
          onClick={handleSaveBookmark}
          disabled={bookmarkLoading || !bookmarkName.trim()}
          startIcon={<AddIcon />}
          sx={{ bgcolor: "#4caf50", "&:hover": { bgcolor: "#45a049" } }}
        >
          Save Current View
        </Button>
        {bookmarkError && (
          <Typography
            variant="caption"
            sx={{ fontSize: 11, color: "#f44336", mt: 0.7, display: "block" }}
          >
            {bookmarkError}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Bookmarks List (paged) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Saved Bookmarks ({bookmarkTotal})
        </Typography>
        {bookmarkPages > 1 && (
          <Typography variant="caption" sx={{ fontSize: 11, color: "#888" }}>
            Page {bookmarkPage} of {bookmarkPages}
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", minHeight: 120 }}>
        {bookmarkLoading && bookmarks.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
            <CircularProgress />
          </Box>
        ) : bookmarks.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, color: "#999" }}>
            <BookmarkIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">No bookmarks saved yet</Typography>
          </Box>
        ) : (
          bookmarks.map((bookmark) => (
            <Paper
              key={bookmark.bookmark_id}
              sx={{
                p: 1,
                mb: 1,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                opacity: bookmarkLoading ? 0.6 : 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LocationOnIcon sx={{ mr: 1, color: BLUE, fontSize: 20 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: 13, fontWeight: 500 }}
                    noWrap
                    title={bookmark.name}
                  >
                    {bookmark.name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: 11, color: "#888" }}>
                    {formatCreated(bookmark.created)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleGoToBookmark(bookmark)}
                  sx={{ color: BLUE, "&:hover": { bgcolor: "#e3f2fd" } }}
                  title="Go to bookmark"
                >
                  <MapIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteBookmark(bookmark.bookmark_id)}
                  disabled={bookmarkLoading}
                  sx={{ color: "#f44336", "&:hover": { bgcolor: "#ffebee" } }}
                  title="Delete bookmark"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      {bookmarkPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <Pagination
            size="small"
            color="primary"
            count={bookmarkPages}
            page={bookmarkPage}
            disabled={bookmarkLoading}
            onChange={(e, p) => loadBookmarks(p)}
            siblingCount={0}
            boundaryCount={1}
          />
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Button
        fullWidth
        variant="outlined"
        startIcon={<CloseIcon />}
        onClick={() => onCloseTool("bookmark")}
        sx={{
          borderColor: "#f44336",
          color: "#f44336",
          "&:hover": { bgcolor: "#ffebee" },
        }}
      >
        Close
      </Button>
    </Box>
  );
}
